import { MessageCircle, Timer, UserPlus, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type { DashboardOverview } from "@/api/db/dashboard";
import type { DashboardPulse } from "@/api/db/dashboard-pulse";
import { DashboardLabels } from "@/lib/labels";
import { cn } from "@/lib/utils";

type Tone = "petrol" | "amber" | "live" | "teal";

const toneClass: Record<Tone, string> = {
	petrol: "bg-primary-soft text-primary",
	amber: "bg-warning-soft text-warning",
	live: "bg-success-soft text-success",
	teal: "bg-info/10 text-info",
};

function durationLabel(ms: number | null) {
	if (ms === null) {
		return "-";
	}

	const minutes = ms / 60_000;

	if (minutes < 1) {
		return `${Math.round(ms / 1000)}s`;
	}

	if (minutes < 10) {
		return `${minutes.toFixed(1).replace(".", ",")} min`;
	}

	if (minutes < 60) {
		return `${Math.round(minutes)} min`;
	}

	return `${Math.round(minutes / 60)}h`;
}

export function TotalsRow({
	totals,
	activeMonth,
	response,
}: {
	totals: DashboardOverview["totals"];
	activeMonth: number;
	response: DashboardPulse["response"];
}) {
	const underHourPct =
		response.total > 0 ? Math.round((response.under_1h / response.total) * 100) : null;

	return (
		<div
			data-component="dashboard-totals"
			className="surface-panel grid grid-cols-1 overflow-hidden sm:grid-cols-2 xl:grid-cols-4"
		>
			<StatCard
				slot="stat-patients"
				icon={Users}
				tone="petrol"
				label={DashboardLabels.stat.patients}
				value={totals.patients}
			/>

			<StatCard
				slot="stat-potential-leads"
				icon={UserPlus}
				tone="amber"
				label={DashboardLabels.stat.potentialLeads}
				value={totals.potential_leads}
			/>

			<StatCard
				slot="stat-active-contacts"
				icon={MessageCircle}
				tone="live"
				label={DashboardLabels.stat.activeContacts}
				value={activeMonth}
			/>

			<StatCard
				slot="stat-response"
				icon={Timer}
				tone="teal"
				label={DashboardLabels.stat.response}
				value={durationLabel(response.median_ms)}
				hint={underHourPct === null ? undefined : DashboardLabels.underHour(underHourPct)}
			/>
		</div>
	);
}

function StatCard({
	slot,
	icon: Icon,
	tone,
	label,
	value,
	hint,
}: {
	slot: string;
	icon: LucideIcon;
	tone: Tone;
	label: string;
	value: string | number;
	hint?: string;
}) {
	return (
		<div
			data-slot={slot}
			data-value={value}
			className="flex min-w-0 items-start gap-4 border-t border-border p-5 first:border-t-0 sm:border-l sm:border-t-0 sm:first:border-l-0"
		>
			<span className={cn("grid size-10 shrink-0 place-items-center rounded-md", toneClass[tone])}>
				<Icon className="size-4" />
			</span>

			<div className="min-w-0">
				<p className="metric-value text-foreground">{value}</p>

				<p title={label} className="mt-2 text-sm leading-snug text-muted-foreground">
					{label}
				</p>

				{hint !== undefined && (
					<p data-slot="hint" className="mt-1 text-xs leading-snug text-muted-foreground">
						{hint}
					</p>
				)}
			</div>
		</div>
	);
}
